import { DatePipe } from "@angular/common";
import { Component, OnDestroy } from "@angular/core";
import { Http } from "@angular/http";
import { Router } from "@angular/router";
import { SelectItem } from "primeng/components/common/api";
import { Observable } from "rxjs";
import { Subscription } from "rxjs/Subscription";
import { environment } from "../environments/environment";
import { AppConstants } from "./core/app.constants";
import { AuthAccessProvider } from "./core/core.auth";
import { BaseResponse, SRMError, User, UserContext } from "./core/core.component";
import { AnomalieService } from "./service/anomalie.service";
import { AssenzeService } from "./service/assenze.service";
import { AuthenticationService } from "./service/authentication.service";
import { BusyProvider } from "./service/busy.service";
import { CheckhrService } from "./service/checkhr.service";
import { EquipmentService } from "./service/equipment.service";
import { GlobalFilterService } from "./service/global-filter.service";
import { MessageProvider } from "./service/message.service";
import { ReportService } from "./service/report.service";
import { UfficioService } from "./service/ufficio.service";
import { SalaService } from "./service/sala.service";
import { UserService } from "./service/user.service";
import { UserCalendarService } from "./service/userCalendar.service";
import { UserOptionsService } from "./service/userOptions.service";
import { DeskManagementService } from "./service/manageDesk.service";
import { DocumentService } from "./service/document.service";
import { GestioneUfficioService } from "./service/gestione-ufficio.service";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.css"],
	providers: [
		AuthenticationService,
		DatePipe,
		BusyProvider,
		AnomalieService,
		UserService,
		AssenzeService,
		DocumentService,
		GlobalFilterService,
		ReportService,
		UserCalendarService,
		CheckhrService,
		UserOptionsService,
		DeskManagementService,
		EquipmentService,
		UfficioService,
		SalaService,
		GestioneUfficioService
	],
})
export class AppComponent implements OnDestroy {
	title = AppConstants.TITLE_APP;
	version: string = AppConstants.VERSION;
	serverVersion: string;
	subscriptions: Subscription[] = new Array();
	message: string;
	warning: string;
	error: string;
	srmError: SRMError;
	userContext: UserContext;
	isUserAdmin: boolean = false;
	amministrazioneEnabled: boolean = false;
	commesseEnabled: boolean = false;
	permitTobeApproved: number = 0;
	adminNotifications: number = 0;

	userToEmbody: SelectItem = null;
	users: User[] = [];
	filteredUsers: SelectItem[] = [];

	constructor(
		public authAccess: AuthAccessProvider,
		private router: Router,
		private authService: AuthenticationService,
		private messageProvider: MessageProvider,
		private userService: UserService,
		private assenzeService: AssenzeService,
		private checkhrService: CheckhrService,
		private busyProvider: BusyProvider,

		private http: Http
	) {
		// Init operations
		this.userContext = this.authAccess.getUserContext();
		this.checkUserAdmin();
		// Message listeners
		this.subscriptions[0] = this.messageProvider.getMessage().subscribe((message) => {
			this.clearMessages();
			this.message = message;
		});
		this.subscriptions[1] = this.messageProvider.getWarning().subscribe((warning) => {
			this.clearMessages();
			this.warning = warning;
		});
		this.subscriptions[2] = this.messageProvider.getError().subscribe((error) => {
			this.clearMessages();
			this.error = error;
		});
		// Authentication listener
		this.subscriptions[3] = this.authAccess.getUserContextAsObservable().subscribe((userContext) => {
			this.userContext = userContext;
			this.checkUserAdmin();
			this.countRequestToBeApproved();
			if (this.isUserAdmin) {
				this.checkAdminNotifications();
			}
		});
		// Request counter routine
		let timer = Observable.timer(1000, AppConstants.FREQUENCY_COUNT_REQUEST_TO_BE_APPROVED);
		this.subscriptions[4] = timer.subscribe((t) => {
			this.countRequestToBeApproved();
		});
		this.subscriptions[5] = this.assenzeService.getRefreshEvent().subscribe(() => {
			this.countRequestToBeApproved();
		});
		// Get data about version
		let url: string = environment.REST_SERVICE_BASE_URL + "/version";
		this.http
			.get(url)
			.map((responseData) => responseData.json())
			.subscribe((result) => {
				this.serverVersion = result.version;
			});
	}

	ngOnDestroy() {
		for (var i = 0; i < this.subscriptions.length; ++i) {
			this.subscriptions[i].unsubscribe();
		}
	}

	private checkUserAdmin() {
		if (this.userContext != null && this.userContext.srmToken != null) {
			this.userService.currentUserInfo(this.userContext.srmToken).subscribe(
				(result) => {
					this.isUserAdmin = result.admin;
					this.amministrazioneEnabled = result.admin || result.equipmentAdmin || result.managedUsers.length > 0;
					this.commesseEnabled = result.admin || result.ordersMgmt;
				},
				(error) => {
					this.srmError.message = error;
				}
			);
		}
	}

	private countRequestToBeApproved() {
		if (this.userContext != null && typeof this.userContext != "undefined") {
			this.assenzeService.getRequestsCount(this.userContext.srmToken, null, "D", null).subscribe(
				(result) => {
					this.permitTobeApproved = result.count;
				},
				(error) => {
					/* ignore */
				}
			);
		}
	}

	private clearMessages() {
		this.message = null;
		this.warning = null;
		this.error = null;
	}

	route(path: string) {
		this.router.navigate(["/" + path]);
	}

	logout() {
		var userCtx = this.authAccess.getUserContext();
		if (userCtx == null) {
			return;
		}
		this.messageProvider.clearAll();
		this.authService.logout(userCtx.srmToken).subscribe(
			(response) => {
				this.authAccess.invalidate();
				this.messageProvider.notifyMessage(AppConstants.SESSION_CLOSED_MSG);
			},
			(error) => {
				this.srmError = error;
				if (!this.srmError.status || !this.srmError.message) {
					this.srmError.message = error;
				}
				this.authAccess.invalidate();
				console.error(this.srmError.message);
			}
		);
	}

	checkAdminNotifications() {
		if (this.userContext != null && this.userContext.srmToken != null) {
			let response: BaseResponse;
			this.checkhrService.getCheckhrAlertsCount(this.userContext.srmToken).subscribe(
				(result) => {
					response = result;
					const alerts = parseInt(response.message, 10);
					if (!isNaN(alerts)) {
						this.adminNotifications = alerts;
					}
				},
				(error) => {
					console.error(this.srmError.message);
				}
			);
		}
	}

	searchUser(event: any) {
		if (!event.query) {
			return;
		}

		let match = event.query.trim();

		this.filteredUsers = AppConstants.filterSuggestions(this.users, match, (u) => [u.fullname]).map((u) => {
			let item: SelectItem = {
				label: u.fullname,
				value: u.id,
			};
			return item;
		});
	}

	selectUser(event: any) {
		this.authAccess.embodyUser(this.userToEmbody.value);
	}

	getUsers() {
		if (this.users.length > 0) {
			return;
		}

		this.busyProvider.showBusyIndicator();
		this.userService.getUsers(this.userContext.srmToken, true, false).subscribe(
			(result) => {
				this.users = result.users.filter((u) => {
					if (u.typeUser !== "U") {
						return false;
					}
					return true;
				});

				this.busyProvider.hideBusyIndicator();
			},
			(error) => {
				this.busyProvider.hideBusyIndicator();
				console.error(error);
			}
		);
	}

	deEmbody() {
		this.authAccess.deEmbodyUser();
	}
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts